import _styled from "styled-components";
import React from "react";

// Ported from the ouster-footer demo (https://ouster-footer.vercel.app/).
// An interactive lidar-style dot grid: resting dots light up into colored
// squares as the cursor sweeps over them, then decay back to rest.
const PARAMS = {
  spacing: 16,
  // grid pitch (px)
  dot: 1.2,
  // resting dot radius (px)
  max: 3.4,
  // peak square half-size (px)
  brush: 70,
  // brush radius — how close the cursor must pass (px)
  falloff: 1.8,
  // brush edge softness (higher = harder edge)
  decay: 1.6,
  // activation decay per second (higher = faster fade)
  rest: 0.18,
  // resting dot alpha
  palette: ["#ff3b30", "#34c759", "#0a84ff"] as const // repeating RGB
};
type Dot = {
  x: number;
  y: number;
  color: string;
  act: number;
};

// Distance from point (px, py) to segment (ax, ay)–(bx, by).
function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number) {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) {
    const ex = px - ax;
    const ey = py - ay;
    return Math.sqrt(ex * ex + ey * ey);
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  const ex = px - cx;
  const ey = py - cy;
  return Math.sqrt(ex * ex + ey * ey);
}
type Props = {
  className?: string;
};
export function FooterDotGrid(props: Props) {
  const {
    className
  } = props;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  React.useEffect(() => {
    const containerEl = containerRef.current;
    const canvasEl = canvasRef.current;
    if (!containerEl || !canvasEl) {
      return;
    }
    const context = canvasEl.getContext("2d");
    if (!context) {
      return;
    }
    // Non-null consts so the narrowed types survive inside the closures below.
    const container = containerEl;
    const canvas = canvasEl;
    const ctx = context;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let dots: Dot[] = [];
    let dpr = window.devicePixelRatio || 1;
    let w = 0;
    let h = 0;
    let visible = false;
    let hoverActive = false;
    const cursor = {
      x: 0,
      y: 0
    };
    let prevCursor: {
      x: number;
      y: number;
    } | null = null;
    let lastFrameMs = 0;
    let raf: number | null = null;
    function assignColor(i: number) {
      return PARAMS.palette[i % PARAMS.palette.length]!;
    }
    function build() {
      const rect = container.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      if (w === 0 || h === 0) {
        dots = [];
        return;
      }
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      dots = [];
      const s = PARAMS.spacing;
      // Overfill by one cell each axis and center the span so dots reach every
      // edge — any dots past the canvas bounds simply clip.
      const cols = Math.max(1, Math.ceil(w / s) + 1);
      const rows = Math.max(1, Math.ceil(h / s) + 1);
      const offX = (w - (cols - 1) * s) / 2;
      const offY = (h - (rows - 1) * s) / 2;
      let i = 0;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          dots.push({
            x: offX + c * s,
            y: offY + r * s,
            color: assignColor(i),
            act: 0
          });
          i++;
        }
      }
    }
    function drawStatic() {
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = `rgba(0,7,4,${PARAMS.rest})`;
      for (const d of dots) {
        ctx.beginPath();
        ctx.arc(d.x, d.y, PARAMS.dot, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    function draw(nowMs: number) {
      raf = null;
      const dt = lastFrameMs ? Math.min(0.05, (nowMs - lastFrameMs) / 1000) : 0;
      lastFrameMs = nowMs;
      ctx.clearRect(0, 0, w, h);
      const restColor = `rgba(0,7,4,${PARAMS.rest})`;
      const decayMul = Math.exp(-PARAMS.decay * dt); // frame-rate independent fade
      let stillAnimating = false;
      for (const d of dots) {
        if (d.act > 0) {
          d.act *= decayMul;
          if (d.act < 0.002) d.act = 0;
        }
        const a = d.act;
        if (a <= 0.001) {
          ctx.fillStyle = restColor;
          ctx.beginPath();
          ctx.arc(d.x, d.y, PARAMS.dot, 0, Math.PI * 2);
          ctx.fill();
        } else {
          const size = PARAMS.dot + (PARAMS.max - PARAMS.dot) * a;
          ctx.globalAlpha = 0.25 + 0.75 * a;
          ctx.fillStyle = d.color;
          ctx.fillRect(d.x - size, d.y - size, size * 2, size * 2);
          ctx.globalAlpha = 1;
          stillAnimating = true;
        }
      }
      if (visible && (stillAnimating || hoverActive)) {
        raf = requestAnimationFrame(draw);
      } else {
        lastFrameMs = 0;
      }
    }
    function kick() {
      if (visible && raf == null) {
        raf = requestAnimationFrame(draw);
      }
    }

    // Paint activation along the cursor segment so fast moves still hit dots in between.
    function paint(ax: number, ay: number, bx: number, by: number) {
      const r = PARAMS.brush;
      const minX = Math.min(ax, bx) - r;
      const maxX = Math.max(ax, bx) + r;
      const minY = Math.min(ay, by) - r;
      const maxY = Math.max(ay, by) + r;
      for (const d of dots) {
        if (d.x < minX || d.x > maxX || d.y < minY || d.y > maxY) continue;
        const dist = distToSegment(d.x, d.y, ax, ay, bx, by);
        if (dist >= r) continue;
        const norm = 1 - dist / r; // 0..1, 1 at cursor center
        const energy = Math.pow(norm, PARAMS.falloff);
        if (energy > d.act) d.act = energy;
      }
    }
    function onEnter(e: MouseEvent) {
      const rect = container.getBoundingClientRect();
      cursor.x = e.clientX - rect.left;
      cursor.y = e.clientY - rect.top;
      prevCursor = {
        x: cursor.x,
        y: cursor.y
      };
      hoverActive = true;
      paint(cursor.x, cursor.y, cursor.x, cursor.y);
      kick();
    }
    function onMove(e: MouseEvent) {
      const rect = container.getBoundingClientRect();
      cursor.x = e.clientX - rect.left;
      cursor.y = e.clientY - rect.top;
      const from = prevCursor || cursor;
      paint(from.x, from.y, cursor.x, cursor.y);
      prevCursor = {
        x: cursor.x,
        y: cursor.y
      };
      kick();
    }
    function onLeave() {
      hoverActive = false;
      prevCursor = null;
      kick();
    }
    build();

    // Reduced motion: render the static grid once, no interaction, no loop.
    if (reduceMotion) {
      const ro = new ResizeObserver(() => {
        dpr = window.devicePixelRatio || 1;
        build();
        drawStatic();
      });
      ro.observe(container);
      drawStatic();
      return () => ro.disconnect();
    }

    // Rebuild on layout-driven size changes (footer height follows the link groups).
    const resizeObserver = new ResizeObserver(() => {
      dpr = window.devicePixelRatio || 1;
      build();
      if (visible) {
        drawStatic();
        kick();
      }
    });
    resizeObserver.observe(container);

    // Only do work while the footer is actually on screen.
    const intersectionObserver = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      visible = entry.isIntersecting;
      if (visible) {
        if (dots.length === 0) build();
        drawStatic();
        kick();
      } else if (raf != null) {
        cancelAnimationFrame(raf);
        raf = null;
        lastFrameMs = 0;
        hoverActive = false;
        prevCursor = null;
      }
    }, {
      rootMargin: "100px"
    });
    intersectionObserver.observe(container);
    container.addEventListener("mouseenter", onEnter);
    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseleave", onLeave);
    return () => {
      if (raf != null) cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      container.removeEventListener("mouseenter", onEnter);
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseleave", onLeave);
    };
  }, []);
  return <div ref={containerRef} className={className} aria-hidden="true">
      <_StyledCanvas ref={canvasRef} />
    </div>;
}
var _StyledCanvas = /*#__PURE__*/_styled("canvas").withConfig({
  displayName: "footer-dot-grid___StyledCanvas",
  componentId: "sc-2ty0ko-0"
})({
  "position": "absolute",
  "inset": "0",
  "display": "block",
  "height": "100%",
  "width": "100%"
});